import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Actions from "../actions/Actions";
import styles from "./Reveal.module.scss";
import "react-pdf/dist/Page/TextLayer.css";
import Button from "@components/button/Button";
import { useStandaloneContent } from "@src/hooks/useStandaloneContent";
import RotateIcon from "@src/assets/icons/rotate.svg?react";
import { isHomeEDApp } from "@src/constants";
import { useMarkLessonComplete } from "@src/hooks";
import AutoCompleteModal from "@src/components/program-details/complete-modal/AutoCompleteModal";
import { resetGoogleTranslateWidget } from "@src/utils/helpers";
import { TranslationIframeWrapper } from "@components/translation-provider";

interface SlideMessage {
  type: string;
  totalSlides?: number;
  currentPage?: number;
  page?: number;
  subPage?: number;
}

type Props = {
  data: string;
  isFullScreen: boolean;
  isPreview?: boolean;
  goBack?: () => void;
  pageNumber: number;
  changePageNumber: (value: number) => void;
  startLesson?: () => void;
};

function ZipRevealSlideView(props: Props) {
  const {
    data,
    isFullScreen,
    isPreview,
    goBack,
    pageNumber,
    changePageNumber,
    startLesson,
  } = props;
  const { totalPages, setTotalPages, lesson, programName, isNewLessonLayout } = useStandaloneContent();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [completeModal, setCompleteModal] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  const onMarkLessonCompleted = useCallback(() => {
    setCompleteModal(false);
  }, []);

  const handleBack = useCallback(() => {
    resetGoogleTranslateWidget();
    goBack?.();
  }, [goBack])

  const { sllMarkLessonComplete, loading: isMarkLessonCompleteLoading } =
    useMarkLessonComplete(onMarkLessonCompleted, handleBack, undefined);

  useEffect(() => {
    // Function to handle messages from iframe
    const handleMessage = (event: MessageEvent<SlideMessage>) => {
      if (event.data.type === "SLIDE_INFO") {
        changePageNumber(event.data.subPage || 1);
        setTotalPages(event.data.totalSlides || 1);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setTotalPages, changePageNumber]);

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage({ type: "FULLSCREEN_TOGGLE", isPreview }, "*");
    }
  }, [isPreview])
  
  useEffect(() => {
    setTotalPages(1);
  }, [data, setTotalPages]);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef?.current?.getBoundingClientRect();
      var w =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      setWidth(w > width ? width : w);
      setHeight(w > 600 ? height : 260);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("orientationchange", calculateSize);

    return () => {
      window.removeEventListener("orientationchange", calculateSize);
    };
  }, [calculateSize]);

  useLayoutEffect(() => {
    window.addEventListener("resize", calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener("resize", calculateSize);
    };
  }, [calculateSize, isFullScreen]);

  useEffect(() => {
    if (width) {
      setTimeout(calculateSize, 50);
    }
  }, [calculateSize, width]);

  const prevDisabled = useMemo(() => pageNumber <= 1, [pageNumber]);
  const isLast = useMemo(
    () => pageNumber >= totalPages,
    [pageNumber, totalPages]
  );

  const handleCompleteLesson = useCallback(async () => {
    if (lesson) await sllMarkLessonComplete(lesson, programName);
    resetGoogleTranslateWidget();
  }, [lesson, sllMarkLessonComplete, programName]);

  const handleLeft = useCallback(() => {
    changePageNumber(Math.max(pageNumber - 1, 1));
    
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage({ type: "PREV_SLIDE" }, "*");
    }
  }, [pageNumber, changePageNumber]);

  const handleRight = useCallback(() => {
    changePageNumber(Math.min(pageNumber + 1, totalPages));

    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage({ type: "NEXT_SLIDE" }, "*");
    }
  }, [totalPages, pageNumber, changePageNumber]);

  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    return (
      <Button
        size={"extraSmall"}
        onClick={handleLeft}
        className={isPreview ? styles.previousBtn : styles.previousBlackBtn}
        label={"PREVIOUS"}
      />
    );
  }, [prevDisabled, isPreview, handleLeft]);

  const nextButton = useMemo(() => {
    if (isLast) return null;
    return <Button size={"extraSmall"} onClick={handleRight} label={"NEXT"} />;
  }, [isLast, handleRight]);

  const iframeSrc = useMemo(() => {
    return isPreview ? `${props.data}/index-clear.html` : `${props.data}/index-black.html`;
  }, [isPreview, props.data])

  return (
    <Actions
      fullScreen={isFullScreen}
      leftButton={prevButton}
      rightButton={nextButton}
      totalPages={totalPages}
      pageNumber={pageNumber}
      isPreview={isPreview}
      goBack={goBack}
    >
      <div
        className={`${styles.wrapper} ${
          !isPreview ? styles.blackfullScreen : ""
        }`}
        ref={wrapperRef}
      >
        <div className={isFullScreen ? styles.fullContent : styles.content}>
          <div
            style={{
              width: `${width}px`,
              height: `${height}px`,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <TranslationIframeWrapper
              ref={iframeRef}
              src={iframeSrc}
              title="Zip Slide"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          </div>
          {isPreview && !isHomeEDApp && !isNewLessonLayout ? (
            <div className={styles.previewBeginLesson}>
              <div className={styles.pulse}>
                <Button
                  label={"Begin Lesson"}
                  color="white"
                  className={styles.beginLesson}
                  onClick={() => startLesson && startLesson()}
                />
              </div>
            </div>
          ) : null}
        </div>
        <div className={styles.rotate}>
          <RotateIcon width="60" height="60" className={styles.rorateIcon} />
          <div className={styles.rotateTitle}>Rotate your phone,</div>
          <div className={styles.rotateDetail}>
            for a better reading experience.
          </div>
        </div>
      </div>
      {completeModal ? (
        <AutoCompleteModal
          open={completeModal}
          loading={isMarkLessonCompleteLoading}
          onClose={() => setCompleteModal(false)}
          onComplete={handleCompleteLesson}
        />
      ) : null}
    </Actions>
  );
}

export default ZipRevealSlideView;
